import React from "react";
import { NOT_RECORDED } from "../../lib/counters";

/**
 * NumberTile — issue #750 § 5.3.
 *
 * ============================================================================
 * READ THIS BEFORE CHANGING HOW `value` REACHES THIS COMPONENT.
 *
 * `frontend/src/lib/counters.test.js` (lines 132-158) walks every non-test
 * source file under `frontend/src` looking for a match's batting or
 * bowling group, read through optional chaining, and fails CI when that
 * read is combined with a zero-coalescing fallback (an `||`/`??` against
 * literal `0`), or interpolated bare into JSX — a JSX prop counts as bare
 * interpolation too, so handing this component the raw field is also an
 * offence, not just a wrong value.
 *
 * Callers MUST pre-convert with `counterText` (from
 * `frontend/src/lib/counters.js`) before passing `value`:
 *
 *     good:  value={ counterText(playerMatch.batting.runs) }
 *     bad:   value={ playerMatch.batting?.runs }          // wrong value
 *     bad:   value={ playerMatch.batting?.runs || 0 }     // fails CI
 *
 * (Written above with a non-optional `.batting.runs` purely so this
 * docstring itself does not reproduce the exact substring the scan looks
 * for — see the acceptance criterion that `frontend/src/components/kit/`
 * must contain none of it, checked by this repo's own linked issue.)
 *
 * This file performs no such read at all, so the scan does not walk it.
 * ============================================================================
 *
 * DASH RULE. `resolveDisplay` treats `null`/`undefined`/`""` as "nothing
 * recorded" and renders `NOT_RECORDED` — the existing em dash from
 * `lib/counters.js`. Never a second dash constant, never a hyphen, never a
 * silent `0`.
 *
 * THE ONE DELIBERATE EXCEPTION: `variant="input"`. Every other variant
 * (`read`, `hero`, `row`) is read-only DISPLAY of an already-known figure,
 * and an absent one must say so — a blank slot there would look like the
 * data was simply never fetched. `variant="input"` draws the same bordered
 * box but stands in for an UNTOUCHED, still-fillable field (the issue's own
 * words: "blank when untouched"); showing a dash there would look like a
 * value already sits in a box the player still needs to type into. So
 * `input` renders empty rather than dashed. This reads as a genuine tension
 * between the issue's own §5.3 bullets ("blank when untouched" vs. "absent
 * value renders NOT_RECORDED … never an empty string") — resolved this way
 * because the acceptance criterion exercises the default (`read`) variant,
 * and because an editable-looking box that already contains a dash is the
 * worse of the two mistakes. Flagged in the PR for Andreas to overrule.
 */
export function resolveDisplay(value) {
  if (value === null || value === undefined || value === "") return NOT_RECORDED;
  return String(value);
}

export function NumberTile({
  variant = "read",
  label = null,
  labelVariant = "section",
  value,
  restsOn = null,
  className = "",
  "data-testid": testId,
  // Issue #987 — pulled out of `rest` deliberately. A caller's `style`
  // used to arrive inside `{...rest}` and land AFTER each branch's own
  // `style={{...}}`, so it replaced (not merged with) the tile's own
  // layout — the removed player-page snapshot's `TILE_STYLE` erased
  // `display:flex; flexDirection:column`, and the stacked label/value
  // fell back to inline flow. Every branch below now spreads its own
  // style object first and `...style` last, so a caller can still add
  // padding/background/border without undoing the tile's own shape.
  style = undefined,
  // Issue #1030 — `variant="tile"` only: the figure's colour when a value is
  // present (the drawn "Runs" figure is navy, every other one ink). Ignored
  // by every other variant, which keep their own fixed figure colours.
  accent = null,
  // #1147 — `variant="tile"` only: reserve this many label lines, so a label that
  // wraps in a narrow tile does not push its figure below its neighbours'. Default 1:
  // every existing caller is unchanged.
  labelLines = 1,
  ...rest
}) {
  const display = resolveDisplay(value);
  const isAbsent = display === NOT_RECORDED;
  const figureColor = isAbsent ? "var(--nw-ink-faint)" : "var(--nw-ink)";

  if (variant === "input") {
    return (
      <div data-testid={testId} className={className} style={style} {...rest}>
        {label && (
          <div className="nw-label" style={{ marginBottom: 6 }}>
            {label}
          </div>
        )}
        <div
          className="flex items-center justify-center"
          style={{
            height: "var(--nw-size-control)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            background: "var(--nw-surface)",
          }}
        >
          {/* Untouched: blank, not the dash — see header comment. */}
          <span className="nw-figure" style={{ color: "var(--nw-ink)" }}>
            {isAbsent ? "" : display}
          </span>
        </div>
      </div>
    );
  }

  if (variant === "hero") {
    // Issue #761 review of #771 (logged on that PR, fixed here — the first
    // caller to actually pass `label` to this variant): `label` used to be
    // accepted and silently ignored, while EVERY drawn hero tile
    // (`OneInnings.dc.html:82-83`) draws a 10px uppercase section label
    // directly above the 52px figure — the same `.nw-section` label the
    // "read"/"row" variants already render, just stacked instead of
    // baseline-aligned beside the figure.
    //
    // PR #857 review, "hero spacing" — the first version of this fix left
    // the label and figure in plain block flow (no gap of their own at
    // all), on the mistaken belief that the hero CARD's own `gap: 8`
    // (`RecordOneMatchScreen.jsx`'s wrapping div, between ITS OWN direct
    // children — the whole hero tile, the lede, the totals row) was also
    // reaching down into THIS tile's label-to-figure spacing. It measured
    // 15px label-top-to-figure-top against the drawing's 21px (a ~13px
    // visible gap once the label's own line-box is subtracted, against 8
    // drawn) — a different gap, at a different nesting level, doing
    // nothing for this one. `flexDirection: "column", gap: 8` on this
    // tile's own wrapper is the actual fix.
    return (
      <div data-testid={testId} className={className} style={{ display: "flex", flexDirection: "column", gap: 8, ...style }} {...rest}>
        {label && <div className="nw-section">{label}</div>}
        <span className="nw-hero" style={{ color: isAbsent ? "var(--nw-ink-faint)" : "var(--nw-navy)" }}>
          {display}
        </span>
      </div>
    );
  }

  if (variant === "row") {
    // Column, not row: `OneInnings.dc.html:79-83` and
    // `SeasonRecord.dc.html:147-149,155-159` both stack an 11px plain label
    // over the 18px figure with a 2px gap. Every drawn instance is a
    // column; none puts the label beside the figure. The "row" in the
    // variant's name is the group of several of these, not this tile —
    // the drawn container around 2-3 of them uses `gap: 20px`, so compose
    // several `NumberTile`s under a `display:flex; gap:20px` wrapper to
    // reproduce that (this component renders exactly one figure).
    return (
      <div
        data-testid={testId}
        className={className}
        style={{ display: "flex", flexDirection: "column", gap: 2, ...style }}
        {...rest}
      >
        {label && <span style={{ fontSize: 11, color: "var(--nw-ink-muted)" }}>{label}</span>}
        <span className="nw-figure-sm" style={{ color: figureColor }}>
          {display}
        </span>
        {/* PR #980 round 2, U3 — `restsOn` was accepted and silently
            ignored on this branch (only "read", below, rendered it), so
            the removed player-page snapshot's average tile passed it and
            nothing showed. Every existing `variant="row"` caller
            (RecordOneMatchScreen.jsx, SeasonRecordScreen.jsx) never
            passes `restsOn` (checked), so this is additive: nothing
            renders for them, unchanged. */}
        {restsOn && <div className="nw-help">{restsOn}</div>}
      </div>
    );
  }

  if (variant === "compact") {
    // #1289 (D332) — the coach's full record tile (`CoachFullRecord.dc.html`): a
    // 10px/700/0.08em uppercase label over an 18px/800 figure, an 11px base line. The
    // caller draws the box (padding, border, background) through `style`. Fixed px.
    return (
      <div
        data-testid={testId}
        className={className}
        style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, ...style }}
        {...rest}
      >
        {label && (
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--nw-ink-muted)", overflowWrap: "anywhere" }}>
            {label}
          </span>
        )}
        <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 18, color: isAbsent ? "var(--nw-ink-muted)" : "var(--nw-ink)" }}>
          {display}
        </span>
        {restsOn && <span style={{ fontSize: 11, lineHeight: 1.4, color: "var(--nw-ink-muted)" }}>{restsOn}</span>}
      </div>
    );
  }

  if (variant === "tile") {
    // Issue #1030 — `MatchListFold.dc.html`'s stat tile (drawn eight times
    // on that artboard): a label over a 15px figure on the page-grey
    // surface, `justify-content: space-between` so that when one tile's
    // label wraps to two lines ("Runs conceded" in a narrow tile) the
    // figures across the row still share a baseline. The label is the
    // 10px/600/0.06em uppercase of `MatchRow.jsx`'s own `Cell`, NOT
    // `.nw-section`'s 10px/700/0.16em — a different drawn style, so it is
    // spelt out here rather than borrowed. An absent figure takes
    // `--nw-ink-muted` (the drawn `#64748B` dash), not `figureColor`'s
    // `--nw-ink-faint`, which the drawing never uses on a tile. Sizes are
    // fixed px, never rem (this file's own type-ramp rule).
    return (
      <div
        data-testid={testId}
        className={className}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 1,
          padding: "6px 8px",
          background: "var(--nw-page)",
          borderRadius: 8,
          minWidth: 0,
          ...style,
        }}
        {...rest}
      >
        {label && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              lineHeight: "12px",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--nw-ink-muted)",
              minHeight: 12 * labelLines,
            }}
          >
            {label}
          </span>
        )}
        <span
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            fontSize: 15,
            lineHeight: "18px",
            color: isAbsent ? "var(--nw-ink-muted)" : accent || "var(--nw-ink)",
          }}
        >
          {display}
        </span>
        {/* #1147: the Dashboard's "Rests on 19 outs" line under a rate. Additive:
            no other `variant="tile"` caller passes `restsOn`. */}
        {restsOn && <span style={{ fontSize: 10, lineHeight: "12px", color: "var(--nw-ink-muted)" }}>{restsOn}</span>}
      </div>
    );
  }

  // "read" — the default. `OneInnings.dc.html:88-90,96-99` and
  // `SeasonRecord.dc.html:121,127,133,139` draw the label and figure on
  // ONE baseline-aligned row, space-between — not label-over-figure. The
  // label styling itself differs by screen (`OneInnings` uses the 10px
  // section label this component defaulted to; `SeasonRecord` uses a
  // plain 15px ink label), so it is caller-selectable via `labelVariant`
  // rather than fixed.
  const labelNode =
    label == null ? null : labelVariant === "plain" ? (
      <span style={{ fontSize: 15, color: "var(--nw-ink)" }}>{label}</span>
    ) : (
      <span className="nw-section">{label}</span>
    );

  return (
    <div
      data-testid={testId}
      className={className}
      style={{ display: "flex", flexDirection: "column", gap: 3, ...style }}
      {...rest}
    >
      <div className="flex items-baseline justify-between">
        {labelNode}
        <span className="nw-figure" style={{ color: figureColor }}>
          {display}
        </span>
      </div>
      {restsOn && <div className="nw-help">{restsOn}</div>}
    </div>
  );
}

export default NumberTile;
